import React, { useEffect, useState } from 'react';
import { Divider, Table, Button, Space, Modal, Form, Popconfirm, Tag, Avatar, Tooltip } from 'antd';
import { CalendarOutlined, PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import AdminPageLayout from './layout/AdminPageLayout';
import useBookingStore from '../../stores/bookingStore';
import dayjs from 'dayjs';
import BookingModal from './common/BookingModal';

const Bookings = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const { bookings, loading, fetchBookings, createBooking, updateBooking, deleteBooking } = useBookingStore();

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const showModal = (booking = null) => {
    setEditingBooking(booking);
    setIsModalVisible(true);
  };

  const handleSaveBooking = async (bookingData) => {
    setSubmitting(true);
    try {
      const result = editingBooking 
        ? await updateBooking(editingBooking.id, bookingData)
        : await createBooking(bookingData);
      
      if (result.success) {
        setIsModalVisible(false);
        setEditingBooking(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'gold',
      confirmed: 'green',
      Reserved: 'green',
      cancelled: 'red',
      completed: 'blue'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (name) => name || '-',
      sorter: (a, b) => {
        const nameA = a.customer_name || '';
        const nameB = b.customer_name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Platform',
      dataIndex: ['platform_details', 'name'],
      key: 'platform_name',
    },
    {
      title: 'Check-in',
      dataIndex: 'check_in',
      key: 'check_in',
      render: (date) => dayjs(date).format('DD-MMM'), // Updated date format
      sorter: (a, b) => dayjs(a.check_in).unix() - dayjs(b.check_in).unix(),
    },
    {
      title: 'Check-out',
      dataIndex: 'check_out',
      key: 'check_out',
      render: (date) => dayjs(date).format('DD-MMM'), // Updated date format
    },
    {
      title: 'Total Price',
      dataIndex: 'total_price',
      key: 'total_price',
      render: (price) => price ? `$${parseFloat(price).toFixed(2)}` : '-',
      sorter: (a, b) => (a.total_price || 0) - (b.total_price || 0),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ width: 90, textAlign: 'center', textTransform: 'capitalize' }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete Booking"
            description="Are you sure?"
            onConfirm={() => deleteBooking(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AdminPageLayout
      title="Bookings"
      icon={<CalendarOutlined />}
      subtitle="Manage your bookings"
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Add Booking
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={bookings}
        loading={loading}
        rowKey="id"
      />

      {/* Use the reusable BookingModal component */}
      <BookingModal
        isVisible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
          setEditingBooking(null);
        }}
        onSave={handleSaveBooking}
        initialValues={editingBooking}
        title={editingBooking ? "Edit Booking" : "Add Booking"}
        submitting={submitting}
      />
    </AdminPageLayout>
  );
};

export default Bookings;
